// Runder Haken-Knopf einer Mitteilung (PROD-Ticket T-364): rechts in der Karte,
// vertikal mittig, etwas größer als eine normale Radio-Box. Abhaken = quittieren
// ohne zu navigieren. Der Kreis wird kurz grün, erst dann meldet der Knopf
// `onAbhaken` — sonst verschwände die Mitteilung, bevor man den Haken sieht.
//
// Inline-Styles statt CSS-Klasse: Das Glocken-Popover wird per Portal außerhalb
// von `.kvp-rd` gerendert, die Klassen greifen dort nicht sicher. Optik nach dem
// Kommentar-Haken `.vg4-ks-hak` (vorgangV4.css), nur größer.
import * as React from "react";
import { Checkmark16Filled } from "@fluentui/react-icons";

/** So lange bleibt der Haken grün sichtbar, bevor die Mitteilung verschwindet. */
export const ABHAKEN_GRUEN_MS = 350;

const OK = "#2f7d5b";
const RAND = "#ddd3dc";

export const NotifAbhaken: React.FC<{
    onAbhaken: () => void;
    /** Beschriftung für Screenreader/Tooltip (Mail: „Als gelesen markieren"). */
    label?: string;
    /** Phone: Trefferfläche 44 px, der sichtbare Kreis bleibt gleich groß. */
    touch?: boolean;
}> = ({ onAbhaken, label = "Als erledigt abhaken", touch = false }) => {
    const [getan, setGetan] = React.useState(false);
    const [hover, setHover] = React.useState(false);
    const timer = React.useRef<number | null>(null);
    // Aktuellen Callback merken, damit der verzögerte Aufruf nicht auf eine
    // veraltete Closure fällt.
    const cb = React.useRef(onAbhaken);
    cb.current = onAbhaken;

    React.useEffect(
        () => () => {
            if (timer.current !== null) window.clearTimeout(timer.current);
        },
        [],
    );

    const abhaken = (e: React.MouseEvent) => {
        // Die Karte darum ist selbst klickbar (öffnet Vorgang/Mail) — nicht mitlaufen.
        e.stopPropagation();
        e.preventDefault();
        if (getan) return; // Doppelklick in der Grün-Phase löst nicht zweimal aus
        setGetan(true);
        timer.current = window.setTimeout(() => {
            timer.current = null;
            cb.current();
        }, ABHAKEN_GRUEN_MS);
    };

    const groesse = 28;
    return (
        <button
            type="button"
            aria-label={label}
            title={label}
            aria-pressed={getan}
            onClick={abhaken}
            // Enter/Leertaste lösen über den nativen Button-Klick aus; die Karte
            // hat aber eigene Tasten-Handler, die sonst zusätzlich navigieren.
            onKeyDown={(e) => e.stopPropagation()}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{
                alignSelf: "center",
                flexShrink: 0,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: touch ? 44 : groesse,
                minHeight: touch ? 44 : groesse,
                padding: 0,
                margin: 0,
                border: "none",
                background: "transparent",
                cursor: "pointer",
            }}
        >
            <span
                aria-hidden
                style={{
                    width: groesse,
                    height: groesse,
                    boxSizing: "border-box",
                    borderRadius: "50%",
                    border: `2px solid ${getan || hover ? OK : RAND}`,
                    background: getan ? OK : "#fff",
                    // Zeichen bleibt im leeren Kreis unsichtbar statt zu fehlen —
                    // so springt beim Abhaken nichts.
                    color: getan ? "#fff" : hover ? OK : "transparent",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "background-color 120ms ease, border-color 120ms ease, color 120ms ease",
                }}
            >
                <Checkmark16Filled />
            </span>
        </button>
    );
};
